import { integer, jsonb, pgTable, text, timestamp, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/**
 * USER PROFILE — the per-user "wildcard" variables the platform keys off the
 * user's UUID (users.id). Exactly one row per user (userId unique). This is the
 * home for the things that were previously hardcoded for a single user: where
 * they can work, where they're based, and where they're looking. (The role/
 * through-line identity is NOT stored here — it stays DERIVED from the DNA
 * corpus, which remains the single source of truth; see lib/userProfile.ts.)
 *
 * Design (per Jason, 2026-06-06):
 *  - DYNAMIC (Q1 #1+#3): fields can be DERIVED (from the corpus / a future
 *    CV/LinkedIn import) and refreshed as new information is digested, OR set
 *    explicitly by the user. `overrides` lists the fields the user locked — a
 *    derive pass must NEVER clobber those. Precedence: user override > derived >
 *    default.
 *  - EXTENSIBLE: `attributes` (jsonb) is the zero-migration bag for future
 *    work-eligibility nuance (visa subclasses, Commonwealth rights, partner
 *    visas, …) so the layered logic engine slots into lib/eligibility.ts WITHOUT
 *    a schema change.
 *  - PLACEHOLDER ON PURPOSE: the work-rights/eligibility logic itself is a coarse
 *    country match + an explicit "not fully baked" disclaimer for now. This table
 *    is the socket; the appliance comes later.
 */
export const userProfiles = pgTable('user_profiles', {
  id: id(),
  userId: text('user_id').notNull().unique(),
  /** ISO-3166 alpha-2 country codes the user holds citizenship in.
   *  e.g. ["US"]  ["BS","US"]  ["CO","US"]. Drives the coarse work-rights default. */
  citizenships: jsonb('citizenships').$type<string[]>().default([]).notNull(),
  /** Country codes the user may work in WITHOUT sponsorship. Seeds from
   *  citizenships, but editable + extensible later (Commonwealth, visas, etc.). */
  workRights: jsonb('work_rights').$type<string[]>().default([]).notNull(),
  /** Where the user is based — now a DERIVED label view off home_geo_id (back-
   *  compat free text; e.g. "Brisbane, AU" / "Miami, US"). Structured geo is the
   *  source of truth; see geo.ts + lib/userProfile.deriveLocationViews. */
  homeLocation: varchar('home_location', { length: 160 }),
  /** Structured "where based" — a GeoToken slug id (geo.ts), the authoritative
   *  location state. countryOf(home_geo_id) feeds eligibility. Nullable: legacy
   *  rows without a mapped token keep home_location only. */
  homeGeoId: varchar('home_geo_id', { length: 48 }),
  /** HYPER-LOCAL (2026-07-06) — the user's declared home METRO/CITY, free
   *  text (e.g. "Miami", "Brisbane"). NEVER a street address (privacy +
   *  honesty — see docs/design/hyper-local-home-2026-07-06.md's grain
   *  ladder). This is the sub-country grain GEO_SEED's closed 14-token seed
   *  doesn't (and by design shouldn't) cover on its own — GEO_SEED stays
   *  minimal/closed per F1's own doc comment, so a user's home city is
   *  captured as its own declared string rather than forcing a match into
   *  (or silently expanding) that seed. Optional; null ⇒ metro-grain
   *  bucketing is INERT (the no-op guarantee — country-grain-only behaviour,
   *  byte-identical to pre-hyper-local). Powers Nearby (home) vs Relocate
   *  bucketing WITHIN a country — see bucketJob.ts's matchesHomeMetro. */
  homeMetro: varchar('home_metro', { length: 120 }),
  /** The state/region/province qualifying home_metro (e.g. "FL", "QLD") —
   *  disambiguates a metro name that recurs across regions (there is more
   *  than one "Springfield"). Optional even when home_metro is set: a
   *  metro-only match is still useful signal, just coarser. Never used
   *  alone — always read alongside home_metro, never as an independent
   *  location fact. */
  homeAdminArea: varchar('home_admin_area', { length: 80 }),
  /** Country/region tokens the user is actively searching — now a DERIVED view
   *  off target_geo_ids. May seed from search_scenarios; kept as the user-level
   *  default. Structured geo (target_geo_ids) is the source of truth. */
  targetMarkets: jsonb('target_markets').$type<string[]>().default([]).notNull(),
  /** Structured "where looking" — GeoToken slug ids (geo.ts). tokensToCountries
   *  derives the ISO targetCountries the matrix reads. Additive, defaulted []. */
  targetGeoIds: jsonb('target_geo_ids').$type<string[]>().default([]).notNull(),
  /** Zero-migration bag for future eligibility nuance + any other per-user
   *  wildcard the logic engine will read later. */
  attributes: jsonb('attributes').$type<Record<string, unknown>>().default({}).notNull(),
  /** Names of fields the USER set explicitly (locked). A derive/refresh pass
   *  skips these so it never overwrites an intentional edit. */
  overrides: jsonb('overrides').$type<string[]>().default([]).notNull(),

  // ── E7 FRONT DOOR (2026-07-17) — docs/diagrams/c4-e7-front-door-2026-07-17.html
  //    + docs/design/first-login-profile-setup-2026-07-09.md. Five additive,
  //    nullable columns backing the 7-card guided first-login flow. Every
  //    one is NULL until the user explicitly answers or confirms that card
  //    (NO INVENTION: a suggestion shown on a card — e.g. the cf-hint home
  //    country, or the OAuth given-name split — is computed at READ time and
  //    never written here until the user's own tap/save does it). Mirrored
  //    in docs/design/e7-front-door-neon.sql.

  /** Card 1 — "What should Meira call you?" Free text, user-set or
   *  user-edited from an OAuth-name suggestion (never silently saved). */
  preferredName: varchar('preferred_name', { length: 120 }),
  /** Card 2 — the CONFIRMED home country, ISO-3166 alpha-2/3 (matches
   *  citizenships/workRights' own shape). Pre-filled on the card as a
   *  SUGGESTION from the request's Cloudflare geo hint (computed live at
   *  GET /api/profile/onboarding time, never stored as a suggestion) — this
   *  column only ever holds the user's own confirmed tap, mirroring
   *  users.dataRegion's detect->confirm discipline (account.routes.ts). Sets
   *  the honest locale default per CLAUDE.md rule 4 (conventions derive from
   *  the TARGET role's country; home country is a separate, honest signal
   *  from that, not a target). */
  homeCountry: varchar('home_country', { length: 56 }),
  /** Card 3 — the R354 postal-code piece: free-text postal/ZIP, paired with
   *  radiusMiles below. Never a full street address. Metro/state
   *  (homeMetro/homeAdminArea above) stay independently settable — this is
   *  the finer, Nearby-radius-driving grain alongside them, not a
   *  replacement. */
  postalCode: varchar('postal_code', { length: 20 }),
  /** Card 3 — the paired 25/50/100-mile radius preference the (separate,
   *  later) Nearby derivation reads. This column only PERSISTS the chip the
   *  user picked; deriving Nearby buckets from it is out of this lane's
   *  scope. */
  radiusMiles: integer('radius_miles'),
  /** Set the moment the 7-card flow is completed OR explicitly skipped-all
   *  (both stamp this the same way — "done with onboarding" is a single
   *  honest state, not two). NULL = never completed/skipped, the gate
   *  profile.routes.ts's GET /api/profile/onboarding reads. No default —
   *  every pre-existing row (every user today) is honestly NULL, never
   *  backfilled to a fabricated completion time. Once set, treated as a
   *  one-way stamp (a second complete/skip-all call is a no-op, never
   *  overwritten) — see computeOnboardingEligibility's own doc comment. */
  onboardedAt: timestamp('onboarded_at', { withTimezone: true }),
  ...timestamps,
});
